import { Search, Filter, X, Users, Loader2, Trash2, Pencil } from 'lucide-react';
import { formatDate } from '@/utils/formatters';
import { describeLoginCell } from './employeeLoginState';
import { useCanWritePeople } from './peopleWriteAccess';
import type { PeopleDirectoryState, LifecycleView } from './usePeopleDirectory';
const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-50 text-green-700',
  inactive: 'bg-red-50 text-red-700',
};

export function PeopleDirectoryList({ state }: { state: PeopleDirectoryState }) {
  const canWrite = useCanWritePeople();
  const { employees, loading, error, search, setSearch, filterStatus, setFilterStatus, filterDepartment, setFilterDepartment, departments, showFilters, setShowFilters, page, setPage, total, limit, totalPages, setEditingEmployeeId, viewFilter, setViewFilter, selectedIds, setSelectedIds, bulkBusy, bulkReport, setBulkReport, selectedCount, allVisibleSelected, someVisibleSelected, toggleOne, toggleAllVisible, fetchEmployees, handleSort, SortIcon, handleDelete, handleBulkDelete } = state;
  return <>
      {/* Search & Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search employees..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm max-sm:min-h-[40px] focus:border-[var(--ink-0)] focus:outline-none focus:ring-1 focus:ring-[var(--ink-0)]"
          />
        </div>
        {/* LBR2-C3 — Active / Past / All lifecycle segregation toggle. */}
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 text-sm max-sm:flex">
          {(['active', 'past', 'all'] as LifecycleView[]).map((v) => (
            <button
              key={v}
              onClick={() => { setViewFilter(v); setPage(1); }}
              className={`rounded-md px-3 py-1.5 capitalize transition-colors max-sm:min-h-[40px] max-sm:flex-1 ${viewFilter === v ? 'bg-[var(--ink-0)] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {v}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors max-sm:min-h-[40px] ${showFilters ? 'border-[var(--ink-0)] bg-[var(--saffron-50)] text-[var(--ink-0)]' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <Filter size={16} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="mb-4 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4">
          {/* LBR2-C3 — Active/Past/All lifecycle status now lives in the segmented
              toggle above; department stays here. */}
          <select
            value={filterDepartment}
            onChange={(e) => { setFilterDepartment(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm max-sm:min-h-[40px] max-sm:w-full focus:border-[var(--ink-0)] focus:outline-none"
          >
            <option value="">All Departments</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          {(filterStatus || filterDepartment) && (
            <button
              onClick={() => { setFilterStatus(''); setFilterDepartment(''); }}
              className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 max-sm:min-h-[40px]"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      )}

      {/* 2026-08-23 — outcome of the last bulk delete. Rendered, not alerted:
          the refusals are the part that needs reading, and an alert cannot be
          re-read once dismissed. */}
      {bulkReport && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                Deleted {bulkReport.hidden} of {bulkReport.requested} selected.
              </p>
              {bulkReport.kept.length > 0 && (
                <>
                  <p className="mt-2 text-gray-700">
                    {bulkReport.kept.length} kept —{' '}
                    <span className="text-gray-500">these were not deleted:</span>
                  </p>
                  <ul className="mt-1 space-y-1">
                    {bulkReport.kept.map((k, i) => (
                      <li key={i} className="text-gray-600">
                        <span className="font-medium text-gray-900">{k.name}</span> — {k.reason}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <button
              onClick={() => setBulkReport(null)}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center"
              title="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* 2026-08-23 — bulk action bar. Only ever visible with a live selection,
          and it always states the count so "delete selected" is never vague. */}
      {selectedCount > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--ink-0)] bg-[var(--saffron-50)] px-4 py-3">
          <span className="text-sm font-medium text-[var(--ink-0)]">
            {selectedCount} of {employees.length} on this page selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 max-sm:min-h-[40px]"
            >
              Clear selection
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkBusy}
              className="flex items-center gap-2 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 max-sm:min-h-[40px]"
            >
              {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete {selectedCount} selected
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
            <span className="ml-2 text-sm text-gray-500">Loading employees...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-red-500">
            <p className="text-sm">{error}</p>
            <button onClick={fetchEmployees} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Retry</button>
          </div>
        ) : employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Users size={40} className="mb-2" />
            <p className="text-sm">No employees found</p>
            <p className="text-xs">Add your first employee to get started</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {/* 🔒 Select-all covers the VISIBLE rows only. `indeterminate`
                      when part of the page is selected, so the control never
                      claims a state it is not in. */}
                  <th className="w-10 px-4 py-3 max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:w-11 max-sm:min-w-[44px] max-sm:bg-gray-50 max-sm:px-3">
                    <input
                      type="checkbox"
                      aria-label={
                        allVisibleSelected
                          ? `Deselect all ${employees.length} employees on this page`
                          : `Select all ${employees.length} employees on this page`
                      }
                      title={`Select the ${employees.length} employees on this page`}
                      checked={allVisibleSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleSelected;
                      }}
                      onChange={toggleAllVisible}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-[var(--ink-0)] max-sm:h-5 max-sm:w-5"
                    />
                  </th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('employeeCode')}>
                    <span className="flex items-center gap-1">Code <SortIcon field="employeeCode" /></span>
                  </th>
                  <th className="cursor-pointer px-4 py-3 max-sm:sticky max-sm:left-11 max-sm:z-10 max-sm:bg-gray-50 max-sm:shadow-[1px_0_0_#e5e7eb]" onClick={() => handleSort('firstName')}>
                    <span className="flex items-center gap-1">Name <SortIcon field="firstName" /></span>
                  </th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Designation</th>
                  <th className="px-4 py-3">Department</th>
                  <th className="cursor-pointer px-4 py-3" onClick={() => handleSort('dateOfJoining')}>
                    <span className="flex items-center gap-1">Date of Joining <SortIcon field="dateOfJoining" /></span>
                  </th>
                  <th className="px-4 py-3">Status</th>
                  {/* 2026-08-23 — employee-first identity: can this person sign in? */}
                  <th className="px-4 py-3">Login</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employees.map((emp) => (
                  <tr
                    key={emp.id}
                    className={`transition-colors ${selectedIds.has(emp.id) ? 'bg-[var(--saffron-50)]' : 'hover:bg-gray-50'}`}
                  >
                    <td className={`px-4 py-3 max-sm:sticky max-sm:left-0 max-sm:z-10 max-sm:w-11 max-sm:min-w-[44px] max-sm:px-3 ${selectedIds.has(emp.id) ? 'max-sm:bg-[var(--saffron-50)]' : 'max-sm:bg-white'}`}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${emp.firstName} ${emp.lastName}`}
                        checked={selectedIds.has(emp.id)}
                        onChange={() => toggleOne(emp.id)}
                        className="h-4 w-4 cursor-pointer rounded border-gray-300 accent-[var(--ink-0)] max-sm:h-5 max-sm:w-5"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{emp.employeeCode}</td>
                    <td className={`px-4 py-3 font-medium text-gray-900 max-sm:sticky max-sm:left-11 max-sm:z-10 max-sm:min-w-[120px] max-sm:shadow-[1px_0_0_#e5e7eb] ${selectedIds.has(emp.id) ? 'max-sm:bg-[var(--saffron-50)]' : 'max-sm:bg-white'}`}>
                      {emp.firstName} {emp.lastName}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{emp.email}</td>
                    <td className="px-4 py-3 text-gray-600">{emp.designation || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{emp.department?.name || departments.find(d => d.id === emp.departmentId)?.name || '-'}</td>
                    <td className="px-4 py-3 text-gray-600">{emp.dateOfJoining ? formatDate(emp.dateOfJoining) : '-'}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const status = emp.status || (emp.isActive !== false ? 'active' : 'inactive');
                        // 2026-08-24 — an ACTIVE employee whose record is not fully
                        // filled reads "Onboarding pending". The rule lives on the
                        // SERVER (employee-completeness.ts) and arrives as
                        // `onboardingComplete` + `onboardingMissing`; the page never
                        // keeps a second copy of what "mandatory" means.
                        //
                        // `onboardingComplete === undefined` = the caller is outside
                        // the HR audience, so the field was never sent. Fall through
                        // to the plain status rather than accusing every row of being
                        // incomplete on a permission we simply do not have.
                        const pending = status === 'active' && emp.onboardingComplete === false;
                        if (pending) {
                          const missing = emp.onboardingMissing ?? [];
                          return (
                            <span
                              title={missing.length ? `Missing: ${missing.join(', ')}` : undefined}
                              className="inline-flex cursor-help rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
                            >
                              Onboarding pending{missing.length ? ` (${missing.length})` : ''}
                            </span>
                          );
                        }
                        return (
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const cell = describeLoginCell(emp);
                        const toneClass =
                          cell.tone === 'on' ? 'bg-green-50 text-green-700'
                          : cell.tone === 'off' ? 'bg-amber-50 text-amber-700'
                          : 'bg-gray-100 text-gray-600';
                        return (
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${toneClass}`}>
                            {cell.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* 2026-08-24 — Edit opens the prefilled WIZARD. Safe to flip
                            only now that the login switch renders in the wizard's
                            Identity & Login step off the shared hook: rerouting this
                            button any earlier would have silently removed HR's only way
                            to grant or revoke a login. The old drawer stays in the file,
                            unreachable, so nothing referencing its state breaks. */}
                        {canWrite && <button onClick={() => setEditingEmployeeId(emp.id)} className="rounded p-1 text-gray-400 hover:bg-[var(--saffron-50)] hover:text-[var(--ink-0)] max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center" title="Edit"><Pencil size={14} /></button>}
                        <button onClick={() => handleDelete(emp.id)} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 max-sm:flex max-sm:min-h-[40px] max-sm:min-w-[40px] max-sm:items-center max-sm:justify-center" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700 max-sm:flex-wrap max-sm:gap-2 max-sm:px-0">
          <div className="text-sm text-gray-400">
            Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed max-sm:min-h-[40px]">Previous</button>
            <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-1 text-sm rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed max-sm:min-h-[40px]">Next</button>
          </div>
        </div>
      )}
  </>;
}
